// OGE Compliance Dashboard - Tier 2 users only (RBAC gated)
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

export const OGEComplianceDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [blockedTrades, setBlockedTrades] = useState([]);
  const [prohibitedSecurities, setProhibitedSecurities] = useState([]);
  const [recusals, setRecusals] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // RBAC check - Tier 2 only
    if (user?.role && !['Operations', 'Compliance'].includes(user.role)) {
      navigate('/dashboard');
      return;
    }
    
    loadComplianceData();
  }, [user, navigate]);

  const loadComplianceData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load all compliance data in parallel
      const [blockedRes, prohibitedRes, recusalsRes] = await Promise.all([
        api.get('/compliance/blocked-trades'),
        api.get('/compliance/prohibited'),
        api.get('/compliance/recusals')
      ]);

      setBlockedTrades(blockedRes.data.blocked_trades || []);
      setProhibitedSecurities(prohibitedRes.data.prohibited_securities || []);
      setRecusals(recusalsRes.data.recusals || []);

    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load compliance data');
      console.error('Compliance data load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const exportForm278e = () => {
    // Generate CSV for Form 278e
    const csvContent = [
      ['Symbol', 'Security Name', 'Quantity', 'Value', 'Acquisition Date', 'Type'],
      // TODO: Add actual holdings data when available
      ['Example data - replace with actual holdings']
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `form_278e_preview_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const filteredProhibited = prohibitedSecurities.filter(sec =>
    sec.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
    sec.reason.toLowerCase().includes(searchQuery.toLowerCase()) ||
    sec.sector.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center">
        <p className="text-[#586069]">Loading OGE compliance data...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      {/* Header */}
      <div className="bg-white border-b border-[#E1E4E8]">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <h1 className="text-2xl font-bold text-[#24292E]">OGE Compliance Dashboard</h1>
          <p className="text-sm text-[#586069] mt-1">
            Office of Government Ethics - STOCK Act Compliance
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {error && (
          <div className="bg-[#FFF5F5] border border-[#FEB2B2] rounded px-4 py-3 text-[#C53030]">
            {error}
          </div>
        )}

        {/* Section 1: Conflict Flags */}
        <div className="bg-white rounded-lg border border-[#E1E4E8] p-6">
          <h2 className="text-lg font-semibold text-[#24292E] mb-4">Active Conflict Flags</h2>
          
          {blockedTrades.length === 0 ? (
            <div className="bg-[#E6F7E6] border border-[#28A745] rounded-lg p-4">
              <div className="flex items-center gap-3">
                <svg className="w-6 h-6 text-[#28A745]" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <p className="text-[#22863A] font-medium">No active conflicts detected</p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {blockedTrades.slice(0, 5).map((trade, index) => (
                <div key={index} className="bg-[#FFF5F5] border-l-4 border-[#D73A49] rounded p-4">
                  <div className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-[#D73A49] flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <div className="flex-1">
                      <h3 className="font-semibold text-[#D73A49] mb-1">⛔ Trade Blocked</h3>
                      <p className="text-sm text-[#24292E]">
                        <span className="font-mono font-semibold">{trade.symbol}</span> - {trade.order_type.toUpperCase()} {formatCurrency(trade.amount)}
                      </p>
                      <p className="text-xs text-[#586069] mt-1">
                        Blocked on {formatDate(trade.checked_at)}
                      </p>
                      {trade.warnings && trade.warnings.length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {trade.warnings.map((warning, idx) => (
                            <li key={idx} className="text-xs text-[#C53030]">• {warning}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Section 2: Prohibited Securities List */}
        <div className="bg-white rounded-lg border border-[#E1E4E8] p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[#24292E]">Prohibited Securities</h2>
            <div className="text-sm text-[#586069]">
              {filteredProhibited.length} of {prohibitedSecurities.length} securities
            </div>
          </div>

          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by symbol, reason, or sector..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 pl-10 border border-[#D1D5DB] rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#0366D6] focus:border-transparent"
              />
              <svg className="w-5 h-5 text-[#959DA5] absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F6F8FA] border-b border-[#E1E4E8]">
                  <th className="text-left py-3 px-4 font-medium text-[#24292E]">Symbol</th>
                  <th className="text-left py-3 px-4 font-medium text-[#24292E]">Sector</th>
                  <th className="text-left py-3 px-4 font-medium text-[#24292E]">Reason</th>
                  <th className="text-center py-3 px-4 font-medium text-[#24292E]">Source</th>
                </tr>
              </thead>
              <tbody>
                {filteredProhibited.map((sec, index) => (
                  <tr key={index} className="border-b border-[#E1E4E8] hover:bg-[#F6F8FA]">
                    <td className="py-3 px-4 font-mono font-semibold text-[#D73A49]">
                      {sec.symbol}
                    </td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-1 bg-[#F6F8FA] text-[#586069] text-xs rounded">
                        {sec.sector}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-[#586069]">
                      {sec.reason}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`px-2 py-1 text-xs rounded ${
                        sec.source === 'static' 
                          ? 'bg-[#E1E4E8] text-[#586069]' 
                          : 'bg-[#FFF9E6] text-[#B8860B]'
                      }`}>
                        {sec.source}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Section 3: Recusal Registry */}
        <div className="bg-white rounded-lg border border-[#E1E4E8] p-6">
          <h2 className="text-lg font-semibold text-[#24292E] mb-4">Recusal Registry</h2>
          
          {recusals.length === 0 ? (
            <div className="text-center py-8 text-[#586069]">
              No active recusal positions
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#F6F8FA] border-b border-[#E1E4E8]">
                    <th className="text-left py-3 px-4 font-medium text-[#24292E]">Symbol</th>
                    <th className="text-left py-3 px-4 font-medium text-[#24292E]">Security Name</th>
                    <th className="text-right py-3 px-4 font-medium text-[#24292E]">Position Value</th>
                    <th className="text-left py-3 px-4 font-medium text-[#24292E]">Recusal Reason</th>
                    <th className="text-center py-3 px-4 font-medium text-[#24292E]">Date Entered</th>
                    <th className="text-center py-3 px-4 font-medium text-[#24292E]">Expiry</th>
                  </tr>
                </thead>
                <tbody>
                  {recusals.map((recusal, index) => (
                    <tr key={index} className="border-b border-[#E1E4E8] hover:bg-[#F6F8FA]">
                      <td className="py-3 px-4 font-mono font-semibold text-[#0366D6]">
                        {recusal.symbol}
                      </td>
                      <td className="py-3 px-4 text-[#24292E]">
                        {recusal.security_name}
                      </td>
                      <td className="py-3 px-4 text-right font-semibold text-[#24292E]">
                        {formatCurrency(recusal.position_value)}
                      </td>
                      <td className="py-3 px-4 text-[#586069]">
                        {recusal.recusal_reason}
                      </td>
                      <td className="py-3 px-4 text-center text-[#586069]">
                        {formatDate(recusal.created_at)}
                      </td>
                      <td className="py-3 px-4 text-center text-[#586069]">
                        {recusal.expires_at ? formatDate(recusal.expires_at) : 'Indefinite'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Section 4: Form 278e Preview */}
        <div className="bg-white rounded-lg border border-[#E1E4E8] p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-[#24292E]">Form 278e Data Preview</h2>
              <p className="text-sm text-[#586069] mt-1">
                OGE Financial Disclosure - Annual Filing Preview
              </p>
            </div>
            <button
              onClick={exportForm278e}
              className="px-4 py-2 bg-[#0366D6] hover:bg-[#0256C5] text-white text-sm font-medium rounded transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export CSV
            </button>
          </div>

          <div className="bg-[#F6F8FA] rounded-lg p-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-semibold text-[#24292E] mb-2">Reporting Period</h3>
                <p className="text-sm text-[#586069]">
                  {new Date().getFullYear()} Calendar Year
                </p>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[#24292E] mb-2">Filing Deadline</h3>
                <p className="text-sm text-[#586069]">
                  May 15, {new Date().getFullYear() + 1}
                </p>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[#24292E] mb-2">Total Holdings</h3>
                <p className="text-sm text-[#586069]">
                  {/* TODO: Replace with actual count */}
                  Pending data integration
                </p>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-[#24292E] mb-2">Transactions YTD</h3>
                <p className="text-sm text-[#586069]">
                  {/* TODO: Replace with actual count */}
                  Pending data integration
                </p>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-[#E1E4E8]">
              <p className="text-xs text-[#586069] leading-relaxed">
                <strong>Note:</strong> This is a preview for internal compliance review. 
                Official Form 278e must be filed through the OGE electronic filing system (Integrity.gov). 
                Export this data as a reference for completing your annual financial disclosure.
              </p>
            </div>
          </div>
        </div>

        {/* OGE Resources */}
        <div className="bg-[#F6F8FA] border border-[#E1E4E8] rounded-lg p-6">
          <h3 className="text-sm font-semibold text-[#24292E] mb-3">OGE Resources</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a
              href="https://www.oge.gov"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-[#0366D6] hover:text-[#0256C5] transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              OGE.gov
            </a>
            <a
              href="https://www.integrity.gov"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-[#0366D6] hover:text-[#0256C5] transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              Integrity.gov (Filing System)
            </a>
            <a
              href="https://www.oge.gov/web/oge.nsf/stockact"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-[#0366D6] hover:text-[#0256C5] transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
              STOCK Act Guidance
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
